import { useEffect, useState } from "react";
import { useDependencies } from "@/shared/context/DependenciesContext";
import { StorageService } from "@/shared/services/localStorage.service";
import { Theme } from "@/shared/types/themes";

const THEME_STORAGE_KEY = "theme";

const getSystemTheme = (): Theme => {
  const systemTheme = window.matchMedia("(prefers-color-scheme: dark)");
  if (!systemTheme) return "light";
  return systemTheme.matches ? "dark" : "light";
};

const getInitialTheme = (storageService: StorageService): Theme => {
  const storedTheme = storageService.getItem(THEME_STORAGE_KEY) as Theme;
  return storedTheme || getSystemTheme();
};

export const useTheme = () => {
  const { storageService } = useDependencies();
  const [theme, setThemeState] = useState<Theme>(getInitialTheme(storageService));

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    storageService.setItem(THEME_STORAGE_KEY, newTheme);
  };

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleSystemThemeChange = () => {
      const hasStoredTheme = storageService.getItem(THEME_STORAGE_KEY);
      if (!hasStoredTheme) {
        setTheme(getSystemTheme());
      }
    };

    if (!mediaQuery) return;

    mediaQuery.addEventListener("change", handleSystemThemeChange);
    return () => mediaQuery.removeEventListener("change", handleSystemThemeChange);
  }, [theme]);

  return { theme, toggleTheme, setTheme };
};
